import axios from "axios";
import { toast } from "sonner";
import {
  API_BASE,
  clearSession,
  ensureValidToken,
  getToken,
} from "@/lib/authStorage";

export const api = axios.create({
  baseURL: API_BASE,
});

let logoutScheduled = false;

function isLoginRequest(config) {
  const url = config?.url || "";
  return url.includes("/auth/login");
}

function isCredentialFailure(err, config) {
  if (isLoginRequest(config)) return true;
  const msg = err?.response?.data?.message || "";
  return msg.includes("اشتباه") || msg.includes("تېروتنه");
}

function shouldTreatAsSessionExpiry(config, err) {
  if (isLoginRequest(config)) return false;
  if (isCredentialFailure(err, config)) return false;
  if (window.location.pathname === "/login") return false;
  return true;
}

function sessionExpiredMessage() {
  const lang = localStorage.getItem("rp_lang") || "dari";
  return lang === "pashto"
    ? "ستاسو ناسته پای ته ورسېده — بیا ننوځئ"
    : "نشست شما منقضی شد — لطفاً دوباره وارد شوید";
}

function networkErrorMessage() {
  const lang = localStorage.getItem("rp_lang") || "dari";
  return lang === "pashto"
    ? "د شبکې تېروتنه — انټرنېټ وګورئ"
    : "خطای شبکه — اتصال اینترنت را بررسی کنید";
}

function scheduleLogout() {
  if (logoutScheduled) return;
  logoutScheduled = true;
  toast.error(sessionExpiredMessage(), { id: "session-expired" });
  clearSession();
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
  window.setTimeout(() => {
    logoutScheduled = false;
  }, 2000);
}

api.interceptors.request.use(async (config) => {
  if (isLoginRequest(config)) return config;
  const token = await ensureValidToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err.config;
    const sessionExpired = shouldTreatAsSessionExpiry(original, err);
    if (!original || original._retry) {
      if (err.response?.status === 401 && sessionExpired) scheduleLogout();
      return Promise.reject(err);
    }
    if (err.response?.status === 401 && getToken() && sessionExpired) {
      original._retry = true;
      try {
        const token = await ensureValidToken();
        if (token) {
          original.headers.Authorization = `Bearer ${token}`;
          return api(original);
        }
      } catch {
        scheduleLogout();
        return Promise.reject(err);
      }
    }
    if (err.response?.status === 401 && sessionExpired) scheduleLogout();
    return Promise.reject(err);
  },
);

/** Login bypasses axios interceptors so wrong credentials never trigger session-expired logout. */
export async function loginRequest(username, password) {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok) {
    const error = new Error(data?.message || "login_failed");
    error.response = { status: res.status, data };
    throw error;
  }
  return data;
}

function parseLegacyDetail(d) {
  if (typeof d === "string") return d;
  if (d?.message) {
    if (Array.isArray(d.shortages) && d.shortages.length) {
      return `${d.message}: ${d.shortages.map((s) => `${s.name_dari || s.stock_item_id} (${s.available}/${s.required} ${s.unit})`).join("، ")}`;
    }
    return d.message;
  }
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return null;
}

export function apiError(e) {
  const data = e?.response?.data;
  if (data?.message) {
    const shortages = data.details?.shortages;
    if (Array.isArray(shortages) && shortages.length) {
      return `${data.message}: ${shortages.map((s) => `${s.name_dari || s.stock_item_id} (${s.available}/${s.required} ${s.unit})`).join("، ")}`;
    }
    const fields = data.details?.fields;
    if (Array.isArray(fields) && fields.length) {
      return `${data.message}: ${fields.map((f) => f.message).join("، ")}`;
    }
    return data.message;
  }
  const legacy = parseLegacyDetail(data?.detail);
  if (legacy) return legacy;
  if (!e?.response) return networkErrorMessage();
  return e?.message || "خطا رخ داد";
}

export function conflictMessage(e, fallback = "تعارض داده — لطفاً صفحه را به‌روز کنید") {
  const data = e?.response?.data;
  if (data?.message) return data.message;
  const legacy = parseLegacyDetail(data?.detail);
  if (legacy) return legacy;
  return fallback;
}

export async function downloadPdf(path, filename) {
  const token = await ensureValidToken();
  const res = await fetch(`${API_BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (res.status === 401) {
    scheduleLogout();
    throw new Error(sessionExpiredMessage());
  }
  if (!res.ok) {
    let msg = `PDF download failed (${res.status})`;
    try {
      const body = await res.json();
      if (body?.message) msg = body.message;
      else if (body?.detail) msg = typeof body.detail === "string" ? body.detail : body.detail?.message || msg;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export { API_BASE };
